import { apiService } from './api';
import { Assessment, Response, ApiResponse } from '../types';

export interface AssessmentFilters {
  page?: number;
  limit?: number;
  status?: string;
  clientId?: number;
  advisorId?: number;
  search?: string;
  questionnaireLabel?: string;
  sortBy?: string;
  sortOrder?: string;
}

export const assessmentService = {
  async getAssessments(filters: AssessmentFilters = {}): Promise<{
    assessments: Assessment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.clientId) params.append('clientId', filters.clientId.toString());
    if (filters.advisorId) params.append('advisorId', filters.advisorId.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.questionnaireLabel) params.append('questionnaireLabel', filters.questionnaireLabel);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await apiService.get<ApiResponse<{
      assessments: Assessment[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>>(`/assessments?${params.toString()}`);

    if (response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch assessments');
  },

  async getAssessment(id: number): Promise<Assessment> {
    const response = await apiService.get<ApiResponse<{ assessment: Assessment }>>(`/assessments/${id}`);
    if (response.data?.assessment) {
      return response.data.assessment;
    }
    throw new Error(response.message || 'Failed to fetch assessment');
  },

  async getStats(): Promise<{
    activeAssessments: number;
    completedThisMonth: number;
    totalAssessments: number;
  }> {
    const response = await apiService.get<ApiResponse<{
      activeAssessments: number;
      completedThisMonth: number;
      totalAssessments: number;
    }>>(`/assessments/stats`);

    if (response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch assessment stats');
  },

  async createAssessment(data: {
    clientId: number;
    goals?: Record<string, any>;
    questionnaireId?: string;
    questionnaireVersion?: number;
  }): Promise<Assessment> {
    const response = await apiService.post<ApiResponse<{ assessment: Assessment }>>('/assessments', data);
    if (response.data?.assessment) {
      return response.data.assessment;
    }
    throw new Error(response.message || 'Failed to create assessment');
  },

  async updateAssessment(id: number, data: Partial<Assessment>): Promise<Assessment> {
    const response = await apiService.put<ApiResponse<{ assessment: Assessment }>>(`/assessments/${id}`, data);
    if (response.data?.assessment) {
      return response.data.assessment;
    }
    throw new Error(response.message || 'Failed to update assessment');
  },

  async deleteAssessment(id: number): Promise<void> {
    const response = await apiService.delete<ApiResponse<void>>(`/assessments/${id}`);
    if (response.message) {
      throw new Error(response.message);
    }
  },

  async getAssessmentResponses(assessmentId: number): Promise<Response[]> {
    const response = await apiService.get<ApiResponse<{ responses: Response[] }>>(`/assessments/${assessmentId}/responses`);
    if (response.data?.responses) {
      return response.data.responses;
    }
    throw new Error(response.message || 'Failed to fetch assessment responses');
  },

  async saveResponse(assessmentId: number, data: {
    questionId: string;
    answerValue: string;
    notes?: string;
    needsReview?: boolean;
  }): Promise<Response> {
    const response = await apiService.post<ApiResponse<{ response: Response }>>(`/assessments/${assessmentId}/responses`, data);
    if (response.data?.response) {
      return response.data.response;
    }
    throw new Error(response.message || 'Failed to save response');
  },

  async updateResponse(responseId: number, data: {
    answerValue?: string;
    notes?: string;
    needsReview?: boolean;
    flaggedForChat?: boolean;
  }): Promise<Response> {
    // Validation: Ensure at least one field is being updated
    if (
      data.answerValue === undefined &&
      data.notes === undefined &&
      data.needsReview === undefined &&
      data.flaggedForChat === undefined
    ) {
      throw new Error('No data provided to update the response.');
    }

    const response = await apiService.put<ApiResponse<{ response: Response }>>(`/assessments/responses/${responseId}`, data);
    if (response.data?.response) {
      return response.data.response;
    }
    throw new Error(response.message || 'Failed to update response');
  },

  async toggleResponseFlag(assessmentId: number, responseId: number, flag: 'needsReview' | 'flaggedForChat', value: boolean): Promise<Response> {
    const response = await apiService.patch<ApiResponse<{ response: Response }>>(
      `/assessments/${assessmentId}/responses/${responseId}/flags`,
      { [flag]: value }
    );
    if (response.data?.response) {
      return response.data.response;
    }
    throw new Error(response.message || 'Failed to update response flag');
  },

  async updateTextQuestionScore(responseId: number, score: number): Promise<Response> {
    const response = await apiService.patch<ApiResponse<{ response: Response }>>(
      `/assessments/responses/${responseId}/score`,
      { score } // Only update score field (aiScore is for future AI model scoring)
    );
    if (response.data?.response) {
      return response.data.response;
    }
    throw new Error(response.message || 'Failed to update score');
  },

  async deleteResponse(assessmentId: number, responseId: number): Promise<void> {
    const response = await apiService.delete<ApiResponse<void>>(`/assessments/${assessmentId}/responses/${responseId}`);
    if (response.message && !response.success) {
      throw new Error(response.message);
    }
  },

  async calculateAssessmentScore(assessmentId: number): Promise<{
    overallScore: number;
    dimensionScores: Record<string, number>;
  }> {
    const response = await apiService.post<ApiResponse<{
      overallScore: number;
      dimensionScores: Record<string, number>;
    }>>(`/assessments/${assessmentId}/calculate-score`);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to calculate assessment score');
  },

  async completeAssessment(assessmentId: number): Promise<Assessment> {
    const response = await apiService.post<ApiResponse<{ assessment: Assessment }>>(`/assessments/${assessmentId}/complete`);
    if (response.data?.assessment) {
      return response.data.assessment;
    }
    throw new Error(response.message || 'Failed to complete assessment');
  },

  async updateAssessmentGoals(assessmentId: number, goals: Array<{ id: string, text: string, priority: 'high' | 'medium' | 'low' }>): Promise<Assessment> {
    const response = await apiService.patch<ApiResponse<{ assessment: Assessment }>>(`/assessments/${assessmentId}/goals`, { goals });
    if (response.data?.assessment) {
      return response.data.assessment;
    }
    throw new Error(response.message || 'Failed to update assessment goals');
  },

  async inviteUser(assessmentId: number, data: { email?: string; firstName?: string; lastName?: string; userId?: number }): Promise<any> {
    const response = await apiService.post<ApiResponse<any>>(`/collaboration/assessment/${assessmentId}/invite`, data);
    if (response.success) {
      return response.data || response;
    }
    throw new Error(response.message || 'Failed to invite user');
  },
};
